import { Vehicle } from '../api/types';
import { ProgressionActivities } from '../pages/home/utilities/constants';
import { ITransferStatus } from '../pages/home/utilities/transferStatusBuilder';
import { PAGE_BEHAVIOR_OPTIONS } from './constants';

const DEVTOOLS_UFT_TRANSFER_STATUS_KEY = 'kmx-devtools-uft-transfer-status';
const ONE_DAY_MS = 86400000;

interface IStoredTransferStatus {
    expiresAt: number;
    value: ITransferStatus;
}

const buildSampleTransferStatus = (): ITransferStatus => ({
    status: ProgressionActivities.TransferOrderApproved,
    appointmentType: 'Transfer',
    vehicle: {
        stockNumber: 24984182,
        year: 2020,
        make: 'Acura',
        model: 'MDX',
        trim: 'Advance',
        basePrice: 32998,
        originalPrice: 34498,
        mileage: 42123,
        engineType: 'V6',
        storeName: 'Richmond',
        stateAbbreviation: 'VA',
        isReserved: true,
        isComingSoon: false,
        hasPriceDrop: false,
        minEstimatedTransferDurationInDays: 2,
        maxEstimatedTransferDurationInDays: 5,
    } as Vehicle,
});

const buildSampleTestDriveTransferStatus = (): ITransferStatus => {
    // Set appointment time to 3 days from now at 2:00 PM
    const appointmentDate = new Date();
    appointmentDate.setDate(appointmentDate.getDate() + 3);
    appointmentDate.setHours(14, 0, 0, 0);

    return {
        status: ProgressionActivities.AppointmentScheduled,
        appointmentType: 'TestDrive',
        appointmentTime: appointmentDate.toISOString(),
        vehicle: {
            stockNumber: 24984182,
            year: 2020,
            make: 'Acura',
            model: 'MDX',
            trim: 'Advance',
            basePrice: 32998,
            originalPrice: 34498,
            mileage: 42123,
            engineType: 'V6',
            storeName: 'Richmond',
            stateAbbreviation: 'VA',
            isReserved: true,
            isComingSoon: false,
            hasPriceDrop: false,
            minEstimatedTransferDurationInDays: 2,
            maxEstimatedTransferDurationInDays: 5,
        } as Vehicle,
    };
};

export const updateUftTransferStatus = (option: PAGE_BEHAVIOR_OPTIONS): void => {
    if (typeof window === 'undefined') {
        return;
    }

    if (option === PAGE_BEHAVIOR_OPTIONS.clear || option === PAGE_BEHAVIOR_OPTIONS.ignore) {
        window.localStorage.removeItem(DEVTOOLS_UFT_TRANSFER_STATUS_KEY);
        return;
    }

    if (option === PAGE_BEHAVIOR_OPTIONS.set) {
        const payload: IStoredTransferStatus = {
            expiresAt: Date.now() + ONE_DAY_MS,
            value: buildSampleTransferStatus(),
        };
        window.localStorage.setItem(DEVTOOLS_UFT_TRANSFER_STATUS_KEY, JSON.stringify(payload));
    }

    if (option === PAGE_BEHAVIOR_OPTIONS.setTestDrive) {
        const payload: IStoredTransferStatus = {
            expiresAt: Date.now() + ONE_DAY_MS,
            value: buildSampleTestDriveTransferStatus(),
        };
        window.localStorage.setItem(DEVTOOLS_UFT_TRANSFER_STATUS_KEY, JSON.stringify(payload));
    }
};

export const readUftTransferStatusOverride = (): ITransferStatus | null => {
    if (typeof window === 'undefined') {
        return null;
    }

    const raw = window.localStorage.getItem(DEVTOOLS_UFT_TRANSFER_STATUS_KEY);
    if (!raw) {
        return null;
    }

    try {
        const parsed = JSON.parse(raw) as IStoredTransferStatus;
        if (!parsed?.expiresAt || parsed.expiresAt < Date.now()) {
            window.localStorage.removeItem(DEVTOOLS_UFT_TRANSFER_STATUS_KEY);
            return null;
        }

        return parsed.value;
    } catch {
        window.localStorage.removeItem(DEVTOOLS_UFT_TRANSFER_STATUS_KEY);
        return null;
    }
};

export const hasUftTransferStatusOverride = (): boolean => Boolean(readUftTransferStatusOverride());
